import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Boxes, History, Plus, Save } from "lucide-react"
import {
  useGetItemQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useGetItemGroupsQuery,
  useGetItemPriceHistoryQuery,
  useAddItemPriceMutation,
} from "shared/redux/endpoints/procurementItems"
import { useGetActiveBudgetCategoriesQuery } from "shared/redux/endpoints/procurementV2"
import { useGetPurchaseSuppliersQuery } from "shared/redux/endpoints/procurementPurchases"
import { formatDate } from "./constants"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "./orderSubmitFeedback"
import { RULES, validateFields, hasErrors } from "./fieldRules"
import VendorCombobox from "./components/items/VendorCombobox"
import {
  Page,
  Card,
  Button,
  Checkbox,
  Currency,
  DataTable,
  EmptyState,
  Input,
  NumberInput,
  Select,
} from "./ui"

const FieldGrid = ({ children, min = 220 }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
      gap: 16,
    }}
  >
    {children}
  </div>
)

const emptyPrice = { price: "", vendorId: "", effectiveDate: new Date().toISOString().slice(0, 10) }

// Common unit-of-measure suggestions — not an enum, just a datalist so the
// field stays free text (backend stores whatever string is typed).
const UOM_SUGGESTIONS = [
  "Cái",
  "Bộ",
  "Hộp",
  "Thùng",
  "Cuộn",
  "Gói",
  "Cặp",
  "Bao",
  "Piece",
  "Set",
  "Box",
  "Pack",
  "Pair",
  "Roll",
  "Unit",
  "Kg",
  "Gram",
  "Lít",
  "Mét",
]

// §2.3 Item Detail / Create form. Two tabs: Details (the item record) and
// "Lịch sử giá" (price history, read-only list + a small add-price form).
// The price history tab only makes sense once the item exists — a brand-new
// item shows a hint instead until it's saved once.
const ItemDetailContainer = () => {
  const { id: routeId } = useParams()
  const navigate = useNavigate()
  const [itemId, setItemId] = useState(routeId || null)
  const isEdit = !!itemId
  const [tab, setTab] = useState("details")

  const { data: existing, isFetching } = useGetItemQuery(itemId, { skip: !itemId })
  const [createItem, { isLoading: creating }] = useCreateItemMutation()
  const [updateItem, { isLoading: updating }] = useUpdateItemMutation()
  const busy = creating || updating

  const { data: categories } = useGetActiveBudgetCategoriesQuery({ isActive: true })

  const [form, setForm] = useState({
    itemCode: "",
    name: "",
    categoryId: "",
    itemGroupId: "",
    uom: "",
    defaultVendorId: "",
    standardPrice: "",
    isActive: true,
    // Barrier-removal flags (2026-09) — false lets this item be picked in a
    // "create PO/Payment without Order/PO" (directOrder) line. Default true
    // on create, matching the backend's own default.
    mustHaveOrder: true,
    mustHavePurchase: true,
    // true = standardPrice is the agreed (contract) price with the default
    // vendor, so an order of only such items gets its vendor auto-selected.
    // false = estimate only (IT, furniture…), needs supplier quotes.
    isApplyReferencePrice: true,
  })
  const [errors, setErrors] = useState({})
  const setField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }))
    setErrors((p) => ({ ...p, [k]: null }))
  }

  useEffect(() => {
    if (existing) {
      setForm({
        itemCode: existing.itemCode || "",
        name: existing.name || "",
        categoryId: existing.categoryId || "",
        itemGroupId: existing.itemGroupId || "",
        uom: existing.uom || "",
        defaultVendorId: existing.defaultVendorId || "",
        standardPrice: existing.standardPrice ?? "",
        isActive: existing.isActive ?? true,
        mustHaveOrder: existing.mustHaveOrder ?? true,
        mustHavePurchase: existing.mustHavePurchase ?? true,
        isApplyReferencePrice: existing.isApplyReferencePrice ?? true,
      })
    }
  }, [existing])

  const { data: groups } = useGetItemGroupsQuery({ categoryId: form.categoryId || undefined })

  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: c.id, label: c.name })),
    [categories]
  )
  const groupOptions = useMemo(
    () => (groups || []).map((g) => ({ value: g.id, label: g.name || g.code })),
    [groups]
  )
  const isValid =
    form.itemCode.trim() &&
    form.name.trim() &&
    form.uom.trim() &&
    form.defaultVendorId &&
    form.standardPrice !== "" &&
    Number(form.standardPrice) >= 0 &&
    form.categoryId &&
    form.itemGroupId

  const buildPayload = () => ({
    itemCode: form.itemCode.trim(),
    name: form.name.trim(),
    itemGroupId: form.itemGroupId,
    uom: form.uom.trim(),
    defaultVendorId: form.defaultVendorId,
    standardPrice: Number(form.standardPrice) || 0,
    isActive: !!form.isActive,
    mustHaveOrder: !!form.mustHaveOrder,
    mustHavePurchase: !!form.mustHavePurchase,
    isApplyReferencePrice: !!form.isApplyReferencePrice,
  })

  const handleSave = async () => {
    if (!isValid) return
    const errs = validateFields(form, RULES.item)
    setErrors(errs)
    if (hasErrors(errs)) return
    try {
      if (itemId) {
        await updateItem({ id: itemId, ...buildPayload() }).unwrap()
        showToastSuccess("Item updated.")
      } else {
        const created = await createItem(buildPayload()).unwrap()
        const newId = created?.id
        if (!newId) throw new Error("Item was created, but we couldn't find its ID.")
        setItemId(newId)
        navigate(`/procurement/items/${newId}`, { replace: true })
        showToastSuccess("Item created.")
      }
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't save this item. Please try again."))
    }
  }

  if (isEdit && isFetching) {
    return (
      <Page breadcrumb={["Procurement", "Items"]} title="Item">
        <Card>Loading…</Card>
      </Page>
    )
  }

  return (
    <Page
      breadcrumb={["Procurement", "Items", isEdit ? existing?.itemCode || "Edit" : "New"]}
      title={isEdit ? existing?.name || "Item" : "New Item"}
      subtitle="Master data for the item catalog — used by the Order Request line-item picker."
      actions={
        <Button variant="secondary" onClick={() => navigate("/procurement/items")}>
          Back to Items
        </Button>
      }
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
        <Button variant={tab === "details" ? "primary" : "secondary"} size="sm" onClick={() => setTab("details")}>
          Details
        </Button>
        <Button
          variant={tab === "prices" ? "primary" : "secondary"}
          size="sm"
          leftIcon={<History aria-hidden size={15} />}
          disabled={!isEdit}
          onClick={() => setTab("prices")}
        >
          Price history
        </Button>
      </div>

      {tab === "details" && (
        <Card icon={<Boxes aria-hidden />} title="Item Information">
          <FieldGrid>
            <Input
              label="Item Code"
              required
              value={form.itemCode}
              error={errors.itemCode}
              onChange={(e) => setField("itemCode", e.target.value)}
              disabled={busy}
            />
            <Input
              label="Name"
              required
              value={form.name}
              error={errors.name}
              onChange={(e) => setField("name", e.target.value)}
              disabled={busy}
            />
            <Select
              label="Category"
              required
              value={form.categoryId}
              onChange={(v) => setField("categoryId", v)}
              options={categoryOptions}
              disabled={busy}
            />
            <Select
              label="Group"
              required
              value={form.itemGroupId}
              onChange={(v) => setField("itemGroupId", v)}
              options={groupOptions}
              disabled={busy || !form.categoryId}
              hint={!form.categoryId ? "Select a category first." : undefined}
            />
            <Input
              label="UOM"
              required
              placeholder="e.g. Cái, Bộ, Hộp"
              list="uom-suggestions"
              value={form.uom}
              error={errors.uom}
              onChange={(e) => setField("uom", e.target.value)}
              disabled={busy}
            />
            <datalist id="uom-suggestions">
              {UOM_SUGGESTIONS.map((u) => (
                <option key={u} value={u} />
              ))}
            </datalist>
            <VendorCombobox
              label="Default Vendor"
              required
              value={form.defaultVendorId}
              selectedLabel={existing?.defaultVendorId === form.defaultVendorId ? existing?.defaultVendorName : undefined}
              onChange={(v) => setField("defaultVendorId", v)}
              disabled={busy}
            />
            <NumberInput
              label="Standard Price (VND)"
              required
              min={0}
              value={form.standardPrice}
              onChange={(v) => setField("standardPrice", v)}
              disabled={busy}
            />
            <div style={{ display: "flex", alignItems: "flex-end", gap: 16, paddingBottom: 10, flexWrap: "wrap" }}>
              <Checkbox checked={form.isActive} onChange={(v) => setField("isActive", v)} label="Active" />
              <Checkbox
                checked={!form.mustHaveOrder}
                onChange={(v) => setField("mustHaveOrder", !v)}
                label="Allow PO without Order"
              />
              <Checkbox
                checked={!form.mustHavePurchase}
                onChange={(v) => setField("mustHavePurchase", !v)}
                label="Allow Payment without PO"
              />
              <Checkbox
                checked={form.isApplyReferencePrice}
                onChange={(v) => setField("isApplyReferencePrice", v)}
                label="Áp dụng giá nguyên tắc"
              />
            </div>
          </FieldGrid>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <Button variant="primary" leftIcon={<Save aria-hidden />} disabled={busy || !isValid} onClick={handleSave}>
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </Card>
      )}

      {tab === "prices" && isEdit && <PriceHistoryCard itemId={itemId} />}
    </Page>
  )
}

// createdBy is a string: "supplier" (vendor quoted via the public link),
// "system" (auto-filled), or a staff member's hub user id.
const priceCreatedBy = (r) => {
  if (r.createdBy === "supplier") return r.vendorName ? `${r.vendorName} (NCC)` : "NCC"
  if (r.createdBy === "system") return "Hệ thống"
  return r.createdByName || (r.createdBy ? `User #${r.createdBy}` : "—")
}

const PriceHistoryCard = ({ itemId }) => {
  const { data: history, isFetching } = useGetItemPriceHistoryQuery(itemId)
  const { data: suppliers } = useGetPurchaseSuppliersQuery()
  const [addItemPrice, { isLoading: adding }] = useAddItemPriceMutation()
  const [form, setForm] = useState(emptyPrice)
  const [showForm, setShowForm] = useState(false)

  const vendorOptions = useMemo(
    () => (suppliers || []).map((s) => ({ value: s.id, label: s.name })),
    [suppliers]
  )

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }))

  const handleAdd = async () => {
    if (!form.price || Number(form.price) < 0 || !form.effectiveDate) return
    try {
      await addItemPrice({
        itemId,
        price: Number(form.price),
        vendorId: form.vendorId || undefined,
        effectiveDate: form.effectiveDate,
      }).unwrap()
      showToastSuccess("Price added.")
      setForm(emptyPrice)
      setShowForm(false)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't add this price. Please try again."))
    }
  }

  const columns = [
    { key: "effectiveDate", header: "Effective date", sortable: true, render: (r) => formatDate(r.effectiveDate) },
    {
      key: "price",
      header: "Price",
      align: "right",
      sortable: true,
      value: (r) => r.price,
      render: (r) => <Currency value={r.price} />,
    },
    { key: "vendorName", header: "Vendor", render: (r) => r.vendorName || "—" },
    { key: "createdByName", header: "Created by", render: priceCreatedBy },
  ]

  return (
    <Card
      icon={<History aria-hidden />}
      title="Price history"
      subtitle="Price history for this item — read-only."
      action={
        <Button variant="secondary" size="sm" leftIcon={<Plus aria-hidden />} onClick={() => setShowForm((v) => !v)}>
          {showForm ? "Cancel" : "Add price"}
        </Button>
      }
    >
      {showForm && (
        <div
          style={{
            border: "1px solid var(--pc-border)",
            background: "var(--pc-surface-2)",
            borderRadius: "var(--pc-r-md)",
            padding: 16,
            marginBottom: 16,
          }}
        >
          <FieldGrid min={180}>
            <NumberInput
              label="Price (VND)"
              required
              min={0}
              value={form.price}
              onChange={(v) => setField("price", v)}
              disabled={adding}
            />
            <Select
              label="Vendor"
              value={form.vendorId}
              onChange={(v) => setField("vendorId", v)}
              options={vendorOptions}
              placeholder="Optional"
              disabled={adding}
            />
            <Input
              label="Effective date"
              type="date"
              required
              value={form.effectiveDate}
              onChange={(e) => setField("effectiveDate", e.target.value)}
              disabled={adding}
            />
          </FieldGrid>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <Button
              variant="primary"
              size="sm"
              disabled={adding || !form.price || !form.effectiveDate}
              onClick={handleAdd}
            >
              {adding ? "Saving…" : "Add"}
            </Button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={history || []}
        isLoading={isFetching}
        initialSort={{ key: "effectiveDate", dir: "desc" }}
        empty={<EmptyState title="No price history yet" text="Add the item's first recorded price above." />}
      />
    </Card>
  )
}

export default ItemDetailContainer
